const SUPPORTED_CATEGORIES = new Set(["XRP", "NXRP"]);
export const PAYOUT_HOLIDAY_HOURS_MULTIPLIER = 1.5;

export const payoutNumberOrZero = (value) => {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
};

export const roundPayoutValue = (value) =>
  Math.round((payoutNumberOrZero(value) + Number.EPSILON) * 100) / 100;

export const normalizeIncludeXeroHours = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["false", "no", "0"].includes(normalized)) return false;
    if (["true", "yes", "1"].includes(normalized)) return true;
  }
  return false;
};

// Groups the week's daily records into shops, each shop keeping its own days.
// Day hours are the paid hours of that shift (regular + after + holiday), so
// every shop total adds up to the staff's Total Time for the week.
// The live branch list wins over the code stored on the record, so a renamed
// shop reads correctly and records written before branchCode existed still
// resolve to a name instead of a raw id.
export const buildShopBreakdown = (records = [], branchNames = {}) => {
  const shops = {};

  records.forEach((record) => {
    const branchId = record.branchId || "";
    const hours = roundPayoutValue(
      payoutNumberOrZero(record.regularHrs) +
        payoutNumberOrZero(record.afterHrs) +
        payoutNumberOrZero(record.holidayHrs),
    );

    if (!shops[branchId]) {
      shops[branchId] = {
        branchId,
        code: branchNames[branchId] || record.branchCode || branchId,
        hours: 0,
        days: [],
      };
    }

    shops[branchId].hours += hours;
    shops[branchId].days.push({
      date: record.date || "",
      checkInAt: record.checkInAt ?? null,
      checkOutAt: record.checkOutAt ?? null,
      hours,
    });
  });

  return Object.values(shops)
    .map((shop) => ({
      ...shop,
      hours: roundPayoutValue(shop.hours),
      days: [...shop.days].sort((a, b) => String(a.date).localeCompare(b.date)),
    }))
    .sort((a, b) => String(a.code).localeCompare(b.code));
};

const createStaffWeek = (record) => {
  const category = String(record.category || "").toUpperCase();
  const nonXeroPayrate = payoutNumberOrZero(
    record.nonXeroPayrate ?? record.payrate,
  );
  const xeroPayrate = payoutNumberOrZero(record.xeroPayrate);
  const xeroHours = payoutNumberOrZero(record.xeroHours);

  return {
    id: record.staff,
    fullName: record.staffName || "",
    category,
    payrate: nonXeroPayrate,
    nonXeroPayrate,
    xeroPayrate,
    xeroHours,
    xeroPay: payoutNumberOrZero(record.xeroPay),
    includeXeroHours: normalizeIncludeXeroHours(record.includeXeroHours),
    kiwiSaver: payoutNumberOrZero(record.kiwiSaver ?? record.kiwisaver),
    fuelSupport: payoutNumberOrZero(record.fuelSupport),
    regularHrs: 0,
    afterHrs: 0,
    holidayHrs: 0,
    bonus: 0,
    roundingHr: 0,
    dailyRecords: [],
  };
};

const calculateStaffWeek = (item, payoutStatusMap, branchNames) => {
  const { dailyRecords, ...staffWeek } = item;
  const regularHrs = roundPayoutValue(item.regularHrs);
  const afterHrs = roundPayoutValue(item.afterHrs);
  const holidayHrs = roundPayoutValue(item.holidayHrs);
  const bonus = roundPayoutValue(item.bonus);
  const roundingHr = roundPayoutValue(item.roundingHr);
  const kiwiSaver = roundPayoutValue(item.kiwiSaver);
  const fuelSupport = roundPayoutValue(item.fuelSupport);
  const xeroHours = roundPayoutValue(item.xeroHours);
  const xeroPayrate = roundPayoutValue(item.xeroPayrate);
  const nonXeroPayrate = roundPayoutValue(item.nonXeroPayrate);
  const xeroPay = roundPayoutValue(item.xeroPay);
  const includeXeroHours = normalizeIncludeXeroHours(item.includeXeroHours);
  const totalTime = roundPayoutValue(regularHrs + afterHrs + holidayHrs);
  const totalHrs = roundPayoutValue(
    regularHrs +
      afterHrs +
      holidayHrs * PAYOUT_HOLIDAY_HOURS_MULTIPLIER,
  );
  const gross = roundPayoutValue(totalHrs * nonXeroPayrate + bonus);

  let cash;
  let bank;

  if (item.category === "NXRP") {
    bank = 0;
    cash = roundPayoutValue(gross + fuelSupport - kiwiSaver);
  } else if (includeXeroHours) {
    bank = roundPayoutValue(xeroHours * xeroPayrate);
    cash = roundPayoutValue(
      (totalHrs - xeroHours) * nonXeroPayrate +
        bonus +
        fuelSupport -
        kiwiSaver,
    );
  } else {
    bank = xeroPay;
    cash = roundPayoutValue(
      totalHrs * nonXeroPayrate +
        bonus +
        fuelSupport -
        kiwiSaver -
        xeroPay,
    );
  }

  return {
    ...staffWeek,
    shops: buildShopBreakdown(dailyRecords, branchNames),
    payrate: nonXeroPayrate,
    nonXeroPayrate,
    xeroPayrate,
    xeroHours,
    xeroPay,
    includeXeroHours,
    regularHrs,
    afterHrs,
    holidayHrs,
    bonus,
    kiwiSaver,
    fuelSupport,
    totalTime,
    totalHrs,
    gross,
    totalPayout: roundPayoutValue(cash + bank),
    roundingHr,
    cash,
    bank,
    completed: payoutStatusMap[item.id]?.paid || false,
    paidTime: payoutStatusMap[item.id]?.paidAt || "",
  };
};

export const aggregateWeeklyPayout = (
  dailyRecords = [],
  payoutStatusMap = {},
  branchNames = {},
) => {
  const staffWeeks = {};

  dailyRecords.forEach((record) => {
    const staffId = record.staff;
    const category = String(record.category || "").toUpperCase();
    if (!staffId || !SUPPORTED_CATEGORIES.has(category)) return;

    if (!staffWeeks[staffId]) {
      staffWeeks[staffId] = createStaffWeek(record);
    }

    const staffWeek = staffWeeks[staffId];
    staffWeek.regularHrs += payoutNumberOrZero(record.regularHrs);
    staffWeek.afterHrs += payoutNumberOrZero(record.afterHrs);
    staffWeek.holidayHrs += payoutNumberOrZero(record.holidayHrs);
    staffWeek.bonus += payoutNumberOrZero(record.bonus);
    staffWeek.roundingHr += payoutNumberOrZero(record.roundingHr);
    staffWeek.dailyRecords.push(record);
  });

  return Object.values(staffWeeks).map((item) =>
    calculateStaffWeek(item, payoutStatusMap, branchNames),
  );
};
